// @ts-strict-ignore
import {
	COLLECTION_ITEM_SUBTYPE_TOUR,
	COLLECTION_ITEM_SUBTYPE_LIST,
	COLLECTION_ITEM_SUBTYPE_MENU,
	COLLECTION_ITEM_SUBTYPE_STORY,
	COLLECTION_ITEM_SUBTYPE_WEB,
	COLLECTION_ITEM_SUBTYPE_PANORAMA,
	TabConfig,
} from 'mytours-read-common'

import NavigationService from './NavigationService'
import Settings, { getAccountId, shouldUsePublishedContent } from '../settings/functions'
import { GetCollectionItemsIndexResponseData } from 'mytours-read-api-typescript'
import { matchPath } from 'react-router'
import { getLanguageCodeFromUrl } from './utils'

// Received from backend, declared in views/layouts/mobile_web.html.erb
declare const baseAppPath: string

export const SCANNER_PAGE_SLUG = 'scanner'
export const SETTINGS_PAGE_SLUG = 'settings'
export const PROFILE_PAGE_SLUG = 'profile'
export const PURCHASED_CONTENT_PAGE_SLUG = 'purchased-content'
export const BADGES_PAGE_SLUG = 'badges'
export const FAVOURITES_PAGE_SLUG = 'favourites'
export const GUIDE_CLIENT_COLLECTIONS_PAGE_SLUG = 'collections'
export const GUIDE_CLIENT_SCREENS_PAGE_SLUG = 'screens'
export const CODE_REDEEM_PAGE_SLUG = 'code/redeem'
export const CODE_AUTO_REDEEM_PAGE_SLUG = 'code/auto-redeem'
export const MORE_TAB_SLUG = 'more'
export const BASE_PATH = baseAppPath
export const PAGE_TYPES = [
	COLLECTION_ITEM_SUBTYPE_TOUR,
	COLLECTION_ITEM_SUBTYPE_LIST,
	COLLECTION_ITEM_SUBTYPE_MENU,
	COLLECTION_ITEM_SUBTYPE_STORY,
	COLLECTION_ITEM_SUBTYPE_WEB,
	COLLECTION_ITEM_SUBTYPE_PANORAMA,
]

export type CollectionItemsSlug =
	| GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour
	| GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List
	| GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu

/*
	App route paths
*/
// Base path, include only locale
export const getBasePath = (): string => `${BASE_PATH}/:locale(${Settings.appLanguage.supportedLanguages.join('|')})?`

export const matchPaths = (path: string, tabs: TabConfig[], tabsSlugs: string[]): boolean => {
	const list = [
		matchPath(path, { path: getCollectionItemsRoutePath(tabsSlugs), exact: true }),
		matchPath(path, { path: getTabsIDsStackRoutePath(tabs, tabsSlugs), exact: true }),
		matchPath(path, { path: getTabsStackRoutePath(tabsSlugs), exact: true }),
		matchPath(path, { path: get404RoutePath(tabsSlugs), exact: true }),
		matchPath(path, { path: getCodeRedeemRoutePath(), exact: true }),
		matchPath(path, { path: getCodeAutoRedeemRoutePath(), exact: true }),
		matchPath(path, { path: getScannerStackRoutePath(), exact: true }),
		matchPath(path, { path: getSettingsStackRoutePath(), exact: true }),
		matchPath(path, { path: getProfileStackRoutePath(), exact: true }),
		matchPath(path, { path: getBadgesStackRoutePath(), exact: true }),
		matchPath(path, { path: getPurchasedContentStackRoutePath(), exact: true }),
		matchPath(path, { path: getGuideClientCollectionsStackRoutePath(), exact: true }),
		matchPath(path, { path: getGuideClientScreensStackRoutePath(), exact: true }),
		matchPath(path, { path: getCollectionListRoutePath(tabsSlugs), exact: true }),
		matchPath(path, { path: getCollectionMenuRoutePath(tabsSlugs), exact: true }),
		matchPath(path, { path: getCollectionTourRoutePath(tabsSlugs), exact: true }),
		matchPath(path, { path: getScreenWebRoutePath(tabsSlugs), exact: true }),
		matchPath(path, { path: getScreenStoryRoutePath(tabsSlugs), exact: true }),
		matchPath(path, { path: getScreenPanoramaRoutePath(tabsSlugs), exact: true }),
		matchPath(path, { path: getBasePath(), exact: true }),
	]

	const hasMatch = list.some(Boolean)

	return hasMatch
}

// Tabs path depends on tabs ids
export const getTabsIDsStackRoutePath = (tabs: TabConfig[], tabsSlugs: string[]): string => {
	const tabIds = tabs.map(tab => tab.tabId)
	// filter tab slugs to avoid conficts with ids
	const filterdIds = tabIds.filter(id => ![...tabsSlugs].includes(id?.toString()))
	const tabIdsPath = tabs?.length > 0 ? `/:tabId(${filterdIds.join('|')})?` : ''
	return `${getBasePath()}${tabIdsPath}`
}

// Tabs path, if {tabsSlugs} is empty, then it's the same as base path
export const getTabsStackRoutePath = (tabsSlugs: string[]): string => {
	const tabIndexes = tabsSlugs?.length > 0 ? `/:tabIndex(${tabsSlugs.join('|')})?` : ''
	return `${getBasePath()}${tabIndexes}`
}

// Initial appended to a redemption URL to mark the item type. Absent → collection, 'b' → bundle,
// 'p' → project (whole-app / app-wide code). Mirrors the backend Code#redeem_link initials, which use
// the first letter of the linked type. Matches the native QR schema:
// <projectId>.stqry.link/code/redeem/<code>/<itemId>/<itemTypeInitial>
export const BUNDLE_CODE_TYPE_INITIAL = 'b'
export const PROJECT_CODE_TYPE_INITIAL = 'p'

// Shared param shape for the redeem + auto-redeem routes. Defined once so the two stay identical:
// <code>/<itemId>/<itemTypeInitial?>  (no initial → collection, 'b' → bundle, 'p' → project).
const CODE_REDEEM_ROUTE_PARAMS = ':code/:itemId?/:itemType?'

// Path to code redemption page - modules/lockapp/routes/CodeRedeemRoute
// Format: /code/redeem/<code>/<itemId>/<itemTypeInitial?>.
// URL parsing/building for redemption lives in modules/lockapp/codeRedemption.ts.
export const getCodeRedeemRoutePath = (): string =>
	`${getBasePath()}/${CODE_REDEEM_PAGE_SLUG}/${CODE_REDEEM_ROUTE_PARAMS}`

// Auto-redeem variant of the redeem route: same params, but the component redeems on mount and
// skips the confirmation card. Opt-in per link (see CodeRedeem `autoRedeem`). modules/lockapp/components/CodeRedeem.tsx
export const getCodeAutoRedeemRoutePath = (): string =>
	`${getBasePath()}/${CODE_AUTO_REDEEM_PAGE_SLUG}/${CODE_REDEEM_ROUTE_PARAMS}`

// Path to scanner page - modules/scanner/routes/ScannerRoute
export const getScannerStackRoutePath = (): string => `${getBasePath()}/:tabIndex(${SCANNER_PAGE_SLUG})`

// Path to settings page - modules/settings/routes/SettingsRoute
export const getSettingsStackRoutePath = (): string => `${getBasePath()}/:tabIndex(${SETTINGS_PAGE_SLUG})`

// Path to profile page - modules/settings/routes/ProfileRoute
export const getProfileStackRoutePath = (): string => `${getBasePath()}/:tabIndex(${PROFILE_PAGE_SLUG})`

// Path to badges page - modules/settings/routes/BadgesRoute
export const getBadgesStackRoutePath = (): string => `${getBasePath()}/:tabIndex(${BADGES_PAGE_SLUG})`

// Path to favourites page - modules/settings/routes/FavouritesRoute
export const getFavouritesStackRoutePath = (): string => `${getBasePath()}/:tabIndex(${FAVOURITES_PAGE_SLUG})`

// Path to the Purchased Content full-grid page - modules/purchasedcontent/routes/PurchasedContentRoute
export const getPurchasedContentStackRoutePath = (): string =>
	`${getBasePath()}/:tabIndex(${PURCHASED_CONTENT_PAGE_SLUG})`

// Path to guide client collections page - modules/guideclientcontents/routes/GuideClientContentsRoute
export const getGuideClientCollectionsStackRoutePath = (): string =>
	`${getBasePath()}/:tabIndex(${GUIDE_CLIENT_COLLECTIONS_PAGE_SLUG})`

// Path to guide client screens page - modules/guideclientcontents/routes/GuideClientContentsRoute
export const getGuideClientScreensStackRoutePath = (): string =>
	`${getBasePath()}/:tabIndex(${GUIDE_CLIENT_SCREENS_PAGE_SLUG})`

// Collection list path - modules/collectionlist/routes/CollectionListRoute
export const getCollectionListRoutePath = (tabsSlugs: string[]): string =>
	`${getTabsStackRoutePath(tabsSlugs)}/${COLLECTION_ITEM_SUBTYPE_LIST}/:id/:accountId?`

// Collection menu path - modules/collectionlist/routes/CollectionListRoute
export const getCollectionMenuRoutePath = (tabsSlugs: string[]): string =>
	`${getTabsStackRoutePath(tabsSlugs)}/${COLLECTION_ITEM_SUBTYPE_MENU}/:id/:accountId?`

// Collection tour path - modules/collectionlist/routes/CollectionTourRoute
export const getCollectionTourRoutePath = (tabsSlugs: string[]): string =>
	`${getTabsStackRoutePath(tabsSlugs)}/${COLLECTION_ITEM_SUBTYPE_TOUR}/:id/:accountId?`

// Tour stops path - modules/tours/routes/TourStopsRoute
export const getCollectionItemsRoutePath = (tabsSlugs: string[], showTabs?: boolean): string => {
	let typeList = `${COLLECTION_ITEM_SUBTYPE_TOUR}|${COLLECTION_ITEM_SUBTYPE_LIST}|${COLLECTION_ITEM_SUBTYPE_MENU}`

	if (showTabs != null) {
		typeList = showTabs
			? `${COLLECTION_ITEM_SUBTYPE_LIST}|${COLLECTION_ITEM_SUBTYPE_MENU}`
			: COLLECTION_ITEM_SUBTYPE_TOUR
	}

	const itemList = `${PAGE_TYPES.join('|')}|item`
	return `${getTabsStackRoutePath(tabsSlugs)}/:type(${typeList})/:id/:collectionAccountId?/:item(${itemList})/:itemId?/:accountId?`
}

// Screen story path - modules/screen/routes/ScreenStoryRoute
export const getScreenStoryRoutePath = (tabsSlugs: string[]): string =>
	`${getTabsStackRoutePath(tabsSlugs)}/${COLLECTION_ITEM_SUBTYPE_STORY}/:id/:accountId?`

// Screen web path - modules/screen/routes/ScreenWebRoute
export const getScreenWebRoutePath = (tabsSlugs: string[]): string =>
	`${getTabsStackRoutePath(tabsSlugs)}/${COLLECTION_ITEM_SUBTYPE_WEB}/:id/:accountId?`

// Screen web path - modules/screen/routes/ScreenWebRoute
export const getScreenPanoramaRoutePath = (tabsSlugs: string[]): string =>
	`${getTabsStackRoutePath(tabsSlugs)}/${COLLECTION_ITEM_SUBTYPE_PANORAMA}/:id/:accountId?`
/* --- */

export const get404RoutePath = (tabsSlugs: string[]): string => `${getTabsStackRoutePath(tabsSlugs)}/404`

export const getTabIndex = (index: number): string => `${index + 1}`

export const getTabByIndex = (tabIndex: string, tabs: TabConfig[]): TabConfig => tabs[Number(tabIndex) - 1]

/*
	App links
*/
// Format URL depending on locale
export const formatLanguageUrl = (endUrl?: string, skipPrimary?: boolean): string => {
	let baseUrl = BASE_PATH
	const { currentLocale, primaryLocale, supportedLanguages } = Settings.appLanguage

	const localeByUrl = getLanguageCodeFromUrl(supportedLanguages)
	const locale = currentLocale || localeByUrl

	if (locale && (locale !== primaryLocale || skipPrimary)) {
		baseUrl += `/${locale}`
	}

	return baseUrl + (endUrl || '')
}

export const formatTabsUrl = (locale: string | undefined, tabIndex: number): string => {
	if (!locale) {
		return `${BASE_PATH}/${tabIndex}`
	}

	return `${BASE_PATH}/${locale}/${tabIndex}`
}

// Format URL depending on tabs
export const getTabsUrl = (): string => {
	const tabIndex: string = NavigationService.navigationStack.getActiveTabIndex()
	let url: string = formatLanguageUrl()

	if (tabIndex) {
		url += `/${tabIndex}`
	}

	return url
}

const getAccountIdUrl = (accountId?: number): string => {
	if (accountId && accountId !== getAccountId() && shouldUsePublishedContent()) {
		return `/${accountId}`
	}

	return ''
}

// Link to Collection list
export const getListUrl = (id: number, accountId?: number): string =>
	`${getTabsUrl()}/${COLLECTION_ITEM_SUBTYPE_LIST}/${id}${getAccountIdUrl(accountId)}`

// Link to Collection Menu
export const getMenuUrl = (id: number, accountId?: number): string =>
	`${getTabsUrl()}/${COLLECTION_ITEM_SUBTYPE_MENU}/${id}${getAccountIdUrl(accountId)}`

// Link to Collection tour
export const getTourUrl = (id: number, accountId?: number): string =>
	`${getTabsUrl()}/${COLLECTION_ITEM_SUBTYPE_TOUR}/${id}${getAccountIdUrl(accountId)}`

// Link to Tour stops
export const getCollectionItemsUrl = (
	id: number,
	itemId: number,
	collectionType: CollectionItemsSlug,
	itemType: GetCollectionItemsIndexResponseData.ItemSubtypeEnum | 'item',
	collectionAccountId: number | undefined,
	accountId?: number,
): string =>
	`${getTabsUrl()}/${collectionType}/${id}${getAccountIdUrl(collectionAccountId)}/${itemType}${itemId > -1 ? `/${itemId}` : ''}${getAccountIdUrl(accountId)}`

// Link to Screen story
export const getStoryUrl = (id: number, accountId?: number): string =>
	`${getTabsUrl()}/${COLLECTION_ITEM_SUBTYPE_STORY}/${id}${getAccountIdUrl(accountId)}`

// Link to Screen panorama
export const getPanoramaUrl = (id: number, accountId?: number): string =>
	`${getTabsUrl()}/${COLLECTION_ITEM_SUBTYPE_PANORAMA}/${id}${getAccountIdUrl(accountId)}`

// Link to Screen web
export const getWebUrl = (id: number, accountId?: number): string =>
	`${getTabsUrl()}/${COLLECTION_ITEM_SUBTYPE_WEB}/${id}${getAccountIdUrl(accountId)}`

// Link to Scanner page
export const getScannerUrl = (): string => `${formatLanguageUrl()}/${SCANNER_PAGE_SLUG}`

// Link to Settings page
export const getSettingsUrl = (isDemoMode?: boolean): string =>
	`${formatLanguageUrl()}/${SETTINGS_PAGE_SLUG}${isDemoMode ? '?demo=true' : ''}`

// Link to Profile page
export const getProfileUrl = (): string => `${formatLanguageUrl()}/${PROFILE_PAGE_SLUG}`

// Purchased Content "Browse all" full-grid page.
export const getPurchasedContentUrl = (): string => `${formatLanguageUrl()}/${PURCHASED_CONTENT_PAGE_SLUG}`

// Link to Badges page
export const getBadgesUrl = (): string => `${formatLanguageUrl()}/${BADGES_PAGE_SLUG}`

// Link to Guide client collections page
export const getGuideClientCollectionsUrl = (): string => `${formatLanguageUrl()}/${GUIDE_CLIENT_COLLECTIONS_PAGE_SLUG}`

// Link to Guide client screens page
export const getGuideClientScreensUrl = (): string => `${formatLanguageUrl()}/${GUIDE_CLIENT_SCREENS_PAGE_SLUG}`

// Link to Collection list Item
export const getListItemUrl = (itemId: number, type: string, includeTabIndex?: boolean, accountId?: number): string => {
	if (includeTabIndex) {
		return `${getTabsUrl()}/${type}/${itemId}${getAccountIdUrl(accountId)}`
	}

	return `${formatLanguageUrl()}/${type}/${itemId}${getAccountIdUrl(accountId)}`
}

export const get404Url = (): string => `${formatLanguageUrl()}/404`
